import { useEffect, useState } from "react";

interface PromptCopiedModalProps {
  isOpen: boolean;
  onClose: () => void;
  description?: string;
}

export default function PromptCopiedModal({
  isOpen,
  onClose,
  description,
}: PromptCopiedModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent background scrolling when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!mounted || !isOpen) return null;

  const handleOpenChatGPT = () => {
    window.open("https://chatgpt.com", "_blank", "noopener,noreferrer");
  };

  // Extract first few words of the description
  const getFirstFewWords = (text: string, count = 7) => {
    if (!text) return "";
    const words = text.split(/\s+/);
    if (words.length <= count) return text;
    return words.slice(0, count).join(" ") + "...";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 select-none">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
        onClick={onClose}
      />

      {/* Clean, Perfectly Proportioned Floating Card */}
      <div className="relative w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-6 md:p-8 shadow-2xl z-50 flex flex-col gap-5 animate-in zoom-in-95 duration-300 text-on-surface">
        
        {/* Success Header */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500 dark:text-emerald-400 rounded-full flex items-center justify-center">
            <span className="material-symbols-outlined text-2xl font-black">check_circle</span>
          </div>

          <div className="space-y-1">
            <h3 className="text-lg font-black tracking-tight leading-tight text-slate-800 dark:text-slate-100">
              Prompt Copied!
            </h3>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
              The visual code is ready in your clipboard.
            </p>
          </div>
        </div>

        {/* Visual Strategy Description Preview */}
        {description && (
          <div className="space-y-1 text-center bg-slate-50/50 dark:bg-slate-800/30 p-4.5 rounded-2xl border border-slate-100 dark:border-slate-800/50">
            <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Strategy Copied
            </span>
            <p className="text-xs font-bold text-slate-700 dark:text-slate-200 italic leading-relaxed px-1">
              "{getFirstFewWords(description, 7)}"
            </p>
          </div>
        )}

        {/* Clean Instructions */}
        <div className="text-xs font-bold text-slate-600 dark:text-slate-400 leading-relaxed bg-slate-50/50 dark:bg-slate-800/30 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/50 space-y-2">
          <p className="text-[10px] font-black uppercase tracking-wider text-primary">Instructions:</p>
          <div className="space-y-1.5 text-left">
            <p>1. Click the button below to open ChatGPT.</p>
            <p>2. Upload your logo or only one reference/product image.</p>
            <p>3. Paste (<kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 text-[10px] font-mono shadow-sm">Ctrl+V</kbd>) and submit the prompt.</p>
          </div>
        </div>

        {/* Primary ChatGPT Button */}
        <button
          onClick={handleOpenChatGPT}
          className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-xs uppercase tracking-[0.15em] flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/10 active:scale-95 transition-all cursor-pointer"
        >
          <span className="material-symbols-outlined text-lg">rocket_launch</span>
          Generate on ChatGPT
        </button>

        {/* Dismiss Button */}
        <button
          onClick={onClose}
          className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer py-1"
        >
          Got it, Close
        </button>
      </div>
    </div>
  );
}
